import { useEffect, useRef, useState } from 'react';
import { TeamSelect } from 'modules/user';
import { useTranslation } from 'react-i18next';
import InputPriceRate from 'components/others/InputPriceRate';
import { Alert, Col, Row } from 'react-bootstrap';
import { useSettings } from 'context/SettingsContext';
import { DateTime as DT } from 'luxon';
import ETiny from 'components/editors/ETiny';
import Editor from 'components/editors/Editor';
import { ClientSelected } from 'modules/client';
import type { ClientType } from '@econtractor/types';
import type { NewObjectType } from '../types';
import conf from 'conf/conf';
import SolarIcon from 'components/icons/SolarIcon';

interface NewObjectFormProps {
    newObject: NewObjectType;
    setNewObject: React.Dispatch<React.SetStateAction<NewObjectType | null>>;
    hasActiveProposal?: boolean;
    acceptedLoading?: boolean;
}

const patchNewObject = (
    setNewObject: React.Dispatch<React.SetStateAction<NewObjectType | null>>,
    patch: Partial<NewObjectType> | ((prev: NewObjectType) => Partial<NewObjectType>),
) => {
    setNewObject((prev) => {
        if (!prev) return null;
        const nextPatch = typeof patch === 'function' ? patch(prev) : patch;
        return { ...prev, ...nextPatch };
    });
};

const clientFromNewObject = (obj: NewObjectType): ClientType | null => {
    const clientId = Number(obj.clientId ?? 0);
    if (!clientId) return null;
    return {
        ID: clientId,
        clientName: obj.clientName || obj.objectName || '',
        clientPhone: obj.clientPhone || '',
        clientEmail: obj.clientEmail || '',
        clientAddress: '',
        clientCreated: 0,
        clientDescription: null,
        clientLocation: '',
        clientStatus: 0,
        leadsCount: 0,
        ownerId: 0,
        userEmail: '',
        userName: '',
    };
};

const NewObjectForm = ({
    newObject,
    setNewObject,
    hasActiveProposal = false,
    acceptedLoading = false,
}: NewObjectFormProps) => {
    const { t } = useTranslation(['Objects']);
    const { localSettings } = useSettings();
    const refInputPrice = useRef<any>(null);
    const tomorrow = DT.now().plus({ days: 1 }).endOf('day');
    const refEdit = useRef<any>(null);
    const TinyEditor = (conf.tinyMode ? ETiny : Editor) as any;
    const refInputObjectName = useRef<HTMLInputElement | null>(null);
    const fromLead = Number(newObject.leadId ?? 0) > 0;
    const [selectedClient, setSelectedClient] = useState<ClientType | null>(() =>
        fromLead ? clientFromNewObject(newObject) : null,
    );
    const [showDescription, setShowDescription] = useState(false);

    useEffect(() => {
        patchNewObject(setNewObject, { objectDeadline: tomorrow.toMillis() });
    }, []);

    useEffect(() => {
        if (refInputObjectName.current) {
            refInputObjectName.current.focus();
            refInputObjectName.current.select();
        }
    }, [refInputObjectName]);

    const handleChangeClient = (client: ClientType | null) => {
        if (fromLead) return;
        setSelectedClient(client);

        if (!client) {
            patchNewObject(setNewObject, { clientId: 0 });
            return;
        }

        patchNewObject(setNewObject, (prev) => {
            const currentName = (prev.objectName || '').trim();
            const shouldPrefillName = !currentName || currentName === '-';
            return {
                clientId: client.ID,
                ...(shouldPrefillName ? { objectName: client.clientName || '' } : {}),
            };
        });
    };

    return (
        <Row>
            <Col lg={12}>
                <ClientSelected
                    title={t('labelSetClient')}
                    value={selectedClient}
                    allowNew={!fromLead}
                    disabled={fromLead}
                    clearable={!fromLead}
                    onChange={handleChangeClient}
                />
            </Col>
            <Col lg={12}>
                <div className="mb-3">
                    <label className="form-label text-muted">{t('labelName')}</label>
                    <input
                        autoFocus={true}
                        ref={refInputObjectName}
                        required
                        name="objectName"
                        value={newObject.objectName || ''}
                        onChange={(e) => patchNewObject(setNewObject, { objectName: e.target?.value || '' })}
                        className="form-control"
                    />
                    {!(newObject.objectName || '').trim() ? (
                        <div className="form-text text-danger fs-12 mt-1">
                            {t('nameRequiredHint')}
                        </div>
                    ) : null}
                    {fromLead && !hasActiveProposal && !acceptedLoading ? (
                        <Alert variant="warning" className="mt-2 mb-0 py-2 px-3 fs-12">
                            <SolarIcon name="exclamation-triangle" className="me-2" aria-hidden="true" />
                            {t('noActiveProposalAlert')}
                        </Alert>
                    ) : null}
                </div>
            </Col>
            <Col lg={6}>
                <div className="mb-3">
                    <label className="form-label text-muted">{t('labelPrice')}</label>
                    <InputPriceRate
                        ref={refInputPrice}
                        value={newObject.objectPrice || 0}
                        onValueChange={(val: any) => {
                            const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
                            const price = Number(val?.floatValue ?? val?.value) || 0;
                            patchNewObject(setNewObject, {
                                objectPrice: price,
                                objectPriceRate: Math.round(price * rate * 100) / 100,
                            });
                        }}
                    />
                    {!newObject.objectPrice ? (
                        <div className="form-text text-danger fs-12 mt-1">
                            {t('priceRequiredHint')}
                        </div>
                    ) : null}
                </div>
            </Col>

            <Col lg={6}>
                <div className="mb-3">
                    <label className="form-label text-muted">{t('labelMarketing')}</label>
                    <InputPriceRate
                        value={newObject.objectMarketing || 0}
                        onValueChange={(val: any) => {
                            const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
                            const price = Number(val?.floatValue ?? val?.value) || 0;
                            patchNewObject(setNewObject, {
                                objectMarketing: price,
                                objectMarketingRate: Math.round(price * rate * 100) / 100,
                            });
                        }}
                    />
                </div>
            </Col>

            <Col lg={6}>
                <TeamSelect
                    value={newObject.teamId || null}
                    showEmpty={true}
                    onChange={(opt: any) => {
                        patchNewObject(setNewObject, { teamId: opt?.value || 0 });
                    }}
                />
            </Col>

            <Col lg={6}>
                <div className="mb-3">
                    <label className="form-label text-muted">{t('labelDeadline')}</label>
                    <input
                        className="form-control"
                        type="date"
                        value={
                            (newObject.objectDeadline
                                ? DT.fromMillis(newObject.objectDeadline).toISODate()
                                : tomorrow.toISODate()) || ''
                        }
                        onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            const endOfDayTimestamp = DT.fromISO(val, { zone: 'local' }).endOf('day').toMillis();
                            patchNewObject(setNewObject, { objectDeadline: endOfDayTimestamp });
                        }}
                    ></input>
                </div>
            </Col>

            <div className="mb-3">
                {showDescription ? (
                    <>
                        <label className="form-label text-muted">{t('labelInfo')}</label>
                        <TinyEditor
                            ref={refEdit}
                            value={newObject.objectInfo || ''}
                            onChange={(newValue: string) => {
                                patchNewObject(setNewObject, { objectInfo: newValue });
                            }}
                        />
                    </>
                ) : (
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        onClick={() => setShowDescription(true)}
                    >
                        <SolarIcon name="plus" className="me-1" />
                        {t('btnAddInfo')}
                    </button>
                )}
            </div>
        </Row>
    );
};

export default NewObjectForm;
